import { useState, useRef } from 'react'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaClock } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import styles from './Contact.module.css'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacy: false
  })
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    error: false
  })
  
  const [errors, setErrors] = useState({})
  const [captchaToken, setCaptchaToken] = useState(null)
  const captchaRef = useRef(null)

  // Dynamische openingstijden checker
  const getOpeningStatus = () => {
    const now = new Date()
    const day = now.getDay() // 0 = Zondag, 1 = Maandag, etc.
    const hours = now.getHours()
    const minutes = now.getMinutes()
    const currentTime = hours * 60 + minutes

    // Maandag (1) t/m Vrijdag (5): 08:30 - 16:30
    const openTime = 8 * 60 + 30 // 08:30
    const closeTime = 16 * 60 + 30 // 16:30

    if (day >= 1 && day <= 5) {
      if (currentTime >= openTime && currentTime < closeTime) {
        return { isOpen: true, message: 'Nu geopend' }
      } else if (currentTime < openTime) {
        return { isOpen: false, message: `Vandaag open om 08:30 uur` }
      } else {
        return { isOpen: false, message: 'Vandaag gesloten (morgen open om 08:30)' }
      }
    } else {
      // Weekend
      return { isOpen: false, message: 'Gesloten (maandag weer open om 08:30)' }
    }
  }

  const openingStatus = getOpeningStatus()

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) newErrors.name = 'Naam is verplicht'
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mailadres is verplicht'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Voer een geldig e-mailadres in'
    }
    
    if (!formData.subject) newErrors.subject = 'Kies een onderwerp'
    if (!formData.message.trim()) newErrors.message = 'Bericht is verplicht'
    if (!formData.privacy) newErrors.privacy = 'U moet akkoord gaan met de privacyverklaring'
    if (!captchaToken) newErrors.captcha = 'Voltooi de captcha verificatie'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setFormStatus({ submitted: true, success: false, error: false })
    
    try {
      // Verstuur naar PHP backend op je hosting
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          captchaToken: captchaToken,
        })
      })

      const data = await response.json()

      if (data.success) {
        setFormStatus({ submitted: true, success: true, error: false })
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          privacy: false
        })
        setCaptchaToken(null)
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha()
        }
      } else {
        throw new Error(data.message || 'Email verzenden mislukt')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setFormStatus({ submitted: true, success: false, error: true })
    }
  }

  return (
    <div className={styles.contactPage}>
      <section className={styles.pageHeader}>
        <div className="container">
          <h1 className="fade-in">Neem contact op</h1>
          <p className="fade-in-delay">We helpen u graag verder. Bel, mail of kom langs!</p>
        </div>
      </section>

      <section className={styles.contactInfoSection}>
        <div className="container">
          <div className={styles.contactGrid}>
            <div className={`${styles.contactCard} fade-in`}>
              <div className={styles.contactIcon}>
                <FaClock aria-hidden="true" />
              </div>
              <h3>Openingstijden</h3>
              <p>Maandag t/m vrijdag<br />08:30 - 16:30 uur<br /><small style={{ fontSize: '0.9em', color: '#6B7280' }}></small></p>
              <span className={styles.contactLink} style={{ 
                color: openingStatus.isOpen ? '#27AE60' : '#6B7280',
                fontWeight: openingStatus.isOpen ? '600' : '400'
              }}>
                {openingStatus.isOpen ? '🟢 ' : '🔴 '}{openingStatus.message}
              </span>
            </div>

            <div className={`${styles.contactCard} ${styles.contactCardSecondary} fade-in-delay`}>
              <div className={styles.contactIcon}>
                <FaEnvelope aria-hidden="true" />
              </div>
              <h3>Mail ons</h3>
              <p>Wij reageren binnen 24 uur<br />op werkdagen</p>
              <a href="mailto:info@sobe-zorg.nl" className={styles.contactLink}>info@sobe-zorg.nl</a>
            </div>

            <div className={`${styles.contactCard} fade-in-delay`}>
              <div className={styles.contactIcon}>
                <FaMapMarkerAlt aria-hidden="true" />
              </div>
              <h3>Bezoek ons</h3>
              <p>Herinckhavestraat 1<br />7557 JJ Hengelo</p>
              <a href="https://www.google.com/maps/search/?api=1&query=Herinckhavestraat+1+7557+JJ+Hengelo" target="_blank" rel="noopener noreferrer" className={styles.contactLink}>
                Open in Google Maps →
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.formSection}>
        <div className="container">
          <div className={styles.formGrid}>
            <div className={`${styles.formContainer} fade-in`}>
              <h2>Stuur ons een bericht</h2>
              <p className={styles.formIntro}>
                Vul onderstaand formulier in en wij nemen zo spoedig mogelijk contact met u op.
              </p>

              <form onSubmit={handleSubmit} className={styles.contactForm} noValidate>
                <div className={styles.formGroup}>
                  <label htmlFor="name">Uw naam <span className={styles.required}>*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Bijv. Jan Jansen"
                    className={errors.name ? styles.inputError : ''}
                    aria-required="true"
                  />
                  {errors.name && <span className={styles.errorMessage} role="alert">{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email">E-mailadres <span className={styles.required}>*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jan@voorbeeld.nl"
                    className={errors.email ? styles.inputError : ''}
                    aria-required="true"
                  />
                  {errors.email && <span className={styles.errorMessage} role="alert">{errors.email}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone">Telefoonnummer (optioneel)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12345678"
                  />
                  <span className={styles.helpText}>Handig voor sneller contact</span>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="subject">Onderwerp <span className={styles.required}>*</span></label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? styles.inputError : ''}
                    aria-required="true"
                  >
                    <option value="">Kies een onderwerp...</option>
                    <option value="dagbesteding">Informatie over dagbesteding</option>
                    <option value="begeleiding">Informatie over individuele begeleiding</option>
                    <option value="intake">Intake/kennismaking aanvragen</option>
                    <option value="vervoer">Vraag over vervoer</option>
                    <option value="anders">Anders</option>
                  </select>
                  {errors.subject && <span className={styles.errorMessage} role="alert">{errors.subject}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message">Uw bericht <span className={styles.required}>*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Typ hier uw vraag of bericht..."
                    className={errors.message ? styles.inputError : ''}
                    aria-required="true"
                  />
                  {errors.message && <span className={styles.errorMessage} role="alert">{errors.message}</span>}
                </div>

                <div className={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="privacy"
                    name="privacy"
                    checked={formData.privacy}
                    onChange={handleChange}
                    aria-required="true"
                  />
                  <label htmlFor="privacy">
                    Ik ga akkoord met de <Link to="/privacy" className={styles.privacyLink}>privacyverklaring</Link> en het gebruik van mijn gegevens voor het beantwoorden van mijn vraag. <span className={styles.required}>*</span>
                  </label>
                </div>
                {errors.privacy && <span className={styles.errorMessage} role="alert">{errors.privacy}</span>}

                <div className={styles.captchaContainer}>
                  <HCaptcha
                    sitekey="4b1439b8-d800-4848-b7f4-acf89eec27c2"
                    onVerify={(token) => {
                      setCaptchaToken(token)
                      setErrors(prev => ({ ...prev, captcha: '' }))
                    }}
                    onExpire={() => setCaptchaToken(null)}
                    ref={captchaRef}
                  />
                  {errors.captcha && <span className={styles.errorMessage} role="alert">{errors.captcha}</span>}
                </div>

                <button type="submit" className={styles.submitBtn} disabled={formStatus.submitted && !formStatus.error}>
                  <FaPaperPlane aria-hidden="true" /> Verstuur bericht
                </button>

                {formStatus.success && (
                  <div className={styles.successMessage} role="alert" aria-live="polite">
                    <FaCheckCircle aria-hidden="true" />
                    Bedankt voor uw bericht! Wij nemen spoedig contact met u op.
                  </div>
                )}

                {formStatus.error && (
                  <div className={styles.errorMessageBox} role="alert" aria-live="assertive">
                    <FaExclamationCircle aria-hidden="true" />
                    Er is iets misgegaan. Probeer het later opnieuw.
                  </div>
                )}
              </form>
            </div>

            <div className={`${styles.mapContainer} fade-in-delay`}>
              <h2>Onze locatie</h2>
              <p>U bent van harte welkom om langs te komen. Maak vooraf een afspraak, dan kunnen wij voldoende tijd voor u vrijmaken.</p>

              <div className={styles.mapEmbed}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2438.234!2d6.7945!3d52.2642!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b81e0000000000%3A0x0!2sHerinckhavestraat%201%2C%207557%20JJ%20Hengelo!5e0!3m2!1snl!2snl!4v1234567890123"
                  width="600"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Locatie van Sobé Zorg Twente in Hengelo"
                />
              </div>

              <div className={styles.openingHours}>
                <h3>Openingstijden</h3>
                <ul>
                  <li><strong>Maandag - Vrijdag:</strong> 08:30 - 16:30</li>
                  <li><strong>Weekend:</strong> Gesloten</li>
                  <li><strong>Feestdagen:</strong> Gesloten</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
